import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VerifoneService } from '@principle-theorem/ng-payments';
import {
  ConfirmDialogComponent,
  DialogPresets,
  IConfirmationDialogInput,
  ObservableDataSource,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';
import { Practice } from '@principle-theorem/principle-core';
import {
  IPractice,
  IVerifoneTerminal,
  VerifonePairResult,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  HISTORY_DATE_FORMAT,
  WithRef,
  getError,
  isSameRef,
  multiSort,
  snapshot,
  sortTimestamp,
  uid,
} from '@principle-theorem/shared';
import { Observable, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  EditVerifoneTerminalDialogComponent,
  IEditVerifoneTerminalRequest,
  IEditVerifoneTerminalResponse,
} from '../edit-verifone-terminal-dialog/edit-verifone-terminal-dialog.component';
import {
  IPairVerifoneTerminalDialogRequest,
  PairVerifoneTerminalDialogComponent,
} from '../pair-verifone-terminal-dialog/pair-verifone-terminal-dialog.component';

const TEST_TERMINAL_SERIAL = 'VTT_principledental';
const TEST_TERMINAL_NAME = 'Virtual Test Terminal';

@Component({
  selector: 'pr-practice-verifone-terminals',
  templateUrl: './practice-verifone-terminals.component.html',
  styleUrls: ['./practice-verifone-terminals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PracticeVerifoneTerminalsComponent {
  readonly testTerminalSerial = TEST_TERMINAL_SERIAL;
  readonly dateFormat = HISTORY_DATE_FORMAT;
  practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  terminals$: Observable<WithRef<IVerifoneTerminal>[]>;
  hasTestTerminal$: Observable<boolean>;
  dataSource: ObservableDataSource<WithRef<IVerifoneTerminal>>;
  displayedColumns: string[] = [
    'name',
    'serialNumber',
    'lastActive',
    'actions',
  ];
  isCreatingTestTerminal = signal(false);

  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this.practice$.next(practice);
    }
  }

  constructor(
    private _dialog: MatDialog,
    private _verifone: VerifoneService,
    private _snackBar: MatSnackBar
  ) {
    this.terminals$ = this.practice$.pipe(
      switchMap((practice) => Practice.verifoneTerminals$(practice)),
      multiSort((aTerminal, bTerminal) =>
        sortTimestamp(aTerminal.lastPaired, bTerminal.lastPaired)
      )
    );
    this.dataSource = new ObservableDataSource(this.terminals$);

    this.hasTestTerminal$ = this.terminals$.pipe(
      map((terminals) =>
        terminals.some(
          (terminal) => terminal.serialNumber === TEST_TERMINAL_SERIAL
        )
      )
    );
  }

  async pairNewTerminal(): Promise<void> {
    const practice = await snapshot(this.practice$);
    const data: IPairVerifoneTerminalDialogRequest = {
      practiceRef: practice.ref,
      testModeEnabled: practice.verifoneSettings?.testModeEnabled,
    };

    await this._dialog
      .open<
        PairVerifoneTerminalDialogComponent,
        IPairVerifoneTerminalDialogRequest,
        undefined
      >(PairVerifoneTerminalDialogComponent, DialogPresets.small({ data }))
      .afterClosed()
      .toPromise();
  }

  async editTerminalPair(existing: WithRef<IVerifoneTerminal>): Promise<void> {
    const terminal = await this._openTerminalEdit({
      title: 'Edit Verifone Terminal',
      terminal: existing,
    });
    if (!terminal) {
      return;
    }

    const serialNumberChanged = terminal.serialNumber !== existing.serialNumber;

    if (serialNumberChanged) {
      const terminals = await snapshot(this.terminals$);
      const alreadyPaired = terminals.some(
        (pairedTerminal) =>
          !isSameRef(pairedTerminal, existing) &&
          pairedTerminal.serialNumber === terminal.serialNumber
      );
      if (alreadyPaired) {
        this._snackBar.open(
          'Another terminal with that serial number already exists',
          'Dismiss',
          { duration: 5000 }
        );
        return;
      }

      await this._rePairTerminal(existing, terminal);
    } else {
      await Firestore.patchDoc(existing.ref, { name: terminal.name });
    }
  }

  async deleteTerminalPair(
    terminal: WithRef<IVerifoneTerminal>
  ): Promise<void> {
    const data = confirmationDialogData({
      title: 'Delete Terminal Pair',
      prompt: `Are you sure you want to delete ${terminal.name}?`,
      submitLabel: 'Delete',
      submitColor: 'warn',
    });

    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();

    if (!confirmed) {
      return;
    }
    await Firestore.deleteDoc(terminal.ref);
  }

  async createTestTerminal(): Promise<void> {
    const practice = await snapshot(this.practice$);

    const terminals = await snapshot(this.terminals$);
    const hasExisting = terminals.some(
      (terminal) => terminal.serialNumber === TEST_TERMINAL_SERIAL
    );
    if (hasExisting) {
      this._snackBar.open('Test terminal already exists', 'Dismiss', {
        duration: 5000,
      });
      return;
    }

    this.isCreatingTestTerminal.set(true);
    try {
      const terminal: Pick<IVerifoneTerminal, 'name' | 'uid'> = {
        name: TEST_TERMINAL_NAME,
        uid: uid(true),
      };

      const response = await this._verifone.pairTerminal({
        serialNumber: TEST_TERMINAL_SERIAL,
        practiceRef: practice.ref,
        terminal,
        testModeEnabled: true,
      });

      if (response.result !== VerifonePairResult.Success) {
        this._snackBar.open('Failed to create test terminal', 'Dismiss', {
          duration: 5000,
        });
        return;
      }

      this._snackBar.open('Test terminal created successfully', 'Dismiss', {
        duration: 5000,
      });
    } catch (error) {
      this._snackBar.open(
        `Failed to create test terminal: ${getError(error)}`,
        'Dismiss',
        { duration: 5000 }
      );
    } finally {
      this.isCreatingTestTerminal.set(false);
    }
  }

  private async _openTerminalEdit(
    data: IEditVerifoneTerminalRequest
  ): Promise<IEditVerifoneTerminalResponse | undefined> {
    return this._dialog
      .open<
        EditVerifoneTerminalDialogComponent,
        IEditVerifoneTerminalRequest,
        IEditVerifoneTerminalResponse
      >(EditVerifoneTerminalDialogComponent, DialogPresets.medium({ data }))
      .afterClosed()
      .toPromise();
  }

  private async _rePairTerminal(
    existing: WithRef<IVerifoneTerminal>,
    updates: IEditVerifoneTerminalResponse
  ): Promise<void> {
    const practice = await snapshot(this.practice$);

    try {
      const response = await this._verifone.pairTerminal({
        serialNumber: updates.serialNumber,
        practiceRef: practice.ref,
        terminal: {
          name: updates.name,
          uid: existing.uid,
        },
        testModeEnabled: practice.verifoneSettings?.testModeEnabled,
      });

      if (response.result !== VerifonePairResult.Success) {
        this._snackBar.open('Failed to re-pair terminal', 'Dismiss', {
          duration: 5000,
        });
        return;
      }

      await Firestore.deleteDoc(existing.ref);
      this._snackBar.open('Terminal re-paired successfully', 'Dismiss', {
        duration: 5000,
      });
    } catch (error) {
      this._snackBar.open(
        `Failed to re-pair terminal: ${getError(error)}`,
        'Dismiss',
        { duration: 5000 }
      );
    }
  }
}
